import { useEffect, useState } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TYPES = { ALL: "Tüm İşlemler", AUTH: "Giriş / Çıkış", CREATE: "Ekleme", UPDATE: "Güncelleme", DELETE: "Silme", SUBSCRIPTION: "Abonelik", DATA_ACCESS: "Veri Erişimi" };
const TYPE_STYLE = {
  AUTH: "bg-blue-500/10 text-blue-700", CREATE: "bg-emerald-500/10 text-emerald-700", UPDATE: "bg-amber-500/10 text-amber-700",
  DELETE: "bg-red-500/10 text-red-700", SUBSCRIPTION: "bg-purple-500/10 text-purple-700", DATA_ACCESS: "bg-slate-900 text-white",
};
const dt = (s) => new Date(s).toLocaleString("tr-TR");

export default function LogsPage() {
  const [offices, setOffices] = useState([]);
  const [office, setOffice] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [logs, setLogs] = useState([]);
  const [ips, setIps] = useState([]);

  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {}); }, []);

  useEffect(() => {
    const params = { limit: 300 };
    if (office !== "ALL") params.office_id = office;
    if (type !== "ALL") params.action_type = type;
    api.get("/admin/logs", { params }).then(({ data }) => setLogs(data));
    api.get("/admin/ip-records", { params: office !== "ALL" && office !== "PLATFORM" ? { office_id: office } : {} }).then(({ data }) => setIps(data));
  }, [office, type]);

  return (
    <div className="space-y-6" data-testid="logs-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Güvenlik</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>İşlem & IP Kayıtları</h1>
        <p className="text-slate-500 mt-1">Ofislerin sistemde yaptığı işlemler, işlem zamanı ve IP adresleri.</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Select value={office} onValueChange={setOffice}>
          <SelectTrigger data-testid="logs-office-filter" className="w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tüm Ofisler</SelectItem>
            <SelectItem value="PLATFORM">Platform (Yönetim) İşlemleri</SelectItem>
            {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.office_code})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="logs-type-filter" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="actions">
        <TabsList>
          <TabsTrigger data-testid="tab-actions" value="actions">İşlem Kayıtları ({logs.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-ips" value="ips">IP Kayıtları ({ips.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="actions" className="mt-4">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>{["Zaman", "Ofis", "Kullanıcı", "Tür", "İşlem", "IP", "Sonuç"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t">
                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-slate-600">{dt(l.created_at)}</td>
                    <td className="px-4 py-2.5">{l.office_name ? <><div className="font-medium">{l.office_name}</div><div className="font-mono text-[11px] text-slate-500">{l.office_code}</div></> : <span className="text-slate-400">Platform</span>}</td>
                    <td className="px-4 py-2.5">{l.username || "-"}<div className="text-[11px] text-slate-500">{ROLE_LABEL[l.role] || ""}</div></td>
                    <td className="px-4 py-2.5"><Badge variant="outline" className={`border-0 ${TYPE_STYLE[l.action_type] || ""}`}>{TYPES[l.action_type] || l.action_type}</Badge></td>
                    <td className="px-4 py-2.5">{l.action}{l.detail && <div className="text-[11px] text-slate-500">{l.detail}</div>}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{l.ip}</td>
                    <td className="px-4 py-2.5">{l.success ? <span className="text-emerald-700 text-xs font-semibold">Başarılı</span> : <span className="text-red-600 text-xs font-semibold">Hata ({l.status_code})</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && <div className="p-10 text-center text-slate-400">Kayıt yok.</div>}
          </Card>
        </TabsContent>
        <TabsContent value="ips" className="mt-4">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ip-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>{["Ofis", "IP Adresi", "Kullanıcılar", "İşlem Sayısı", "İlk Görülme", "Son Görülme"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {ips.map((r) => (
                  <tr key={`${r.office_id}-${r.ip}`} className="border-t">
                    <td className="px-4 py-2.5"><div className="font-medium">{r.office_name}</div><div className="font-mono text-[11px] text-slate-500">{r.office_code}</div></td>
                    <td className="px-4 py-2.5 font-mono">{r.ip}</td>
                    <td className="px-4 py-2.5 text-xs">{r.users.join(", ")}</td>
                    <td className="px-4 py-2.5 font-semibold">{r.count}</td>
                    <td className="px-4 py-2.5 text-xs">{dt(r.first_seen)}</td>
                    <td className="px-4 py-2.5 text-xs">{dt(r.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {ips.length === 0 && <div className="p-10 text-center text-slate-400">Kayıt yok.</div>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
